let sdkPromise = null;

/**
 * Dynamically load the Cashfree SDK (CDN) and return the initialized instance.
 * @param {"sandbox"|"production"} mode
 * @returns {Promise<any>}
 */
export const loadCashfreeSDK = async (mode = "sandbox") => {
  if (typeof window === "undefined") {
    throw new Error("Cashfree SDK can only be loaded in the browser");
  }

  const initCashfreeInstance = () => {
    if (!window.Cashfree) {
      throw new Error("Cashfree SDK not available on window");
    }
    return window.Cashfree({ mode });
  };

  // SDK already loaded
  if (window.Cashfree) {
    return initCashfreeInstance();
  }

  if (!sdkPromise) {
    sdkPromise = new Promise((resolve, reject) => {
      const existingScript = document.querySelector('script[data-cashfree-sdk]');
      if (existingScript) {
        existingScript.addEventListener('load', () => resolve(window.Cashfree));
        existingScript.addEventListener('error', reject);
        return;
      }

      const script = document.createElement("script");
      script.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
      script.async = true;
      script.defer = true;
      script.dataset.cashfreeSdk = "true";
      script.onload = () => resolve(window.Cashfree);
      script.onerror = reject;
      document.body.appendChild(script);
    });
  }

  await sdkPromise;
  return initCashfreeInstance();
};

/**
 * Open Cashfree checkout with the provided session ID.
 * @param {Object} options
 * @param {string} options.paymentSessionId
 * @param {"sandbox"|"production"} options.mode
 * @param {string} [options.redirectTarget="_self"]
 */
export const openCashfreeCheckout = async ({
  paymentSessionId,
  mode = "sandbox",
  redirectTarget = "_self"
}) => {
  if (!paymentSessionId) {
    throw new Error("paymentSessionId is required for Cashfree checkout");
  }

  const cashfree = await loadCashfreeSDK(mode);
  return cashfree.checkout({
    paymentSessionId,
    redirectTarget
  });
};
let cachedInstance = null;
let cachedMode = null;

const CASHFREE_SDK_URL = 'https://sdk.cashfree.com/js/v3/cashfree.js';
const SCRIPT_ID = 'cashfree-sdk';

const loadSdkScript = () => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Window is not available'));
  }

  if (document.getElementById(SCRIPT_ID)) {
    return new Promise((resolve, reject) => {
      const existingScript = document.getElementById(SCRIPT_ID);
      if (window.Cashfree) {
        resolve();
        return;
      }
      existingScript.addEventListener('load', () => resolve());
      existingScript.addEventListener('error', (err) => reject(err));
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = CASHFREE_SDK_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = (err) => reject(err);
    document.body.appendChild(script);
  });
};

export const getCashfreeSDK = async (mode = 'sandbox') => {
  if (cachedInstance && cachedMode === mode) {
    return cachedInstance;
  }

  if (!sdkPromise) {
    sdkPromise = loadSdkScript();
  }

  await sdkPromise;

  if (!window.Cashfree) {
    throw new Error('Cashfree SDK failed to load');
  }

  cachedInstance = window.Cashfree({ mode });
  cachedMode = mode;
  return cachedInstance;
};

export const resetCashfreeSDK = () => {
  cachedInstance = null;
  cachedMode = null;
  sdkPromise = null;
};

